import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import {
    BoaTypes,
    IChartModule,
    IForecastHistoryModule,
    IModule,
    ModuleType,
    isModuleWithInfoProps,
    IMetricModule,
    IAncillaryChartModule,
    IGeneralModule,
    BoaIds,
    ILiveTableModule,
} from '@/interfaces/powerMarkets/IModule.ts';
import { IModulePreset, IModulePresetBoaDashboard, ModulePresetType } from '@/interfaces/powerMarkets/IModulePreset.ts';
import { ILiveTableOptions } from '@/interfaces/powerMarkets/table/ILiveTableOptions.ts';
import { cloneDeep } from './global.ts';
import { dateFormatToAnyFormat } from './dateUtils.ts';

export function getModuleType (module: IModule): ModuleType | ModulePresetType | BoaTypes | null {
    if (isModuleWithInfoProps(module)) {
        if ('info' in module && 'ancillary' in module.info) {
            return ModulePresetType.AncillaryMetric;
        }
        if (module.info.type === BoaTypes.BalancingCosts) {
            return ModuleType.Treemap;
        }
        return module.info.type;
    }
    if ('chartSeries' in module) {
        return ModuleType.Chart;
    }
    if ('metricNames' in module) {
        if (module.metricNames.metricType === null) {
            return ModulePresetType.DialMetric;
        }
        return ModulePresetType.Metric;
    }
    return null;
}

export function isBoaTable (module: IModule, liveTableOptions: Record<string, ILiveTableOptions>): boolean {
    const moduleType = getModuleType(module);
    if (moduleType === BoaTypes.BoaDashboard) return true;
    if (moduleType !== ModuleType.LiveTable) return false;
    const moduleTypeString = (module as ILiveTableModule)?.data?.moduleType;
    if (!liveTableOptions || !moduleTypeString) return false;
    const options = Object.values(liveTableOptions).find((option) => option.moduleType === moduleTypeString);
    return !!options?.getOnPeriodChange;
}

export function isDayRangeLiveTable (module: IModule, liveTableOptions: Record<string, ILiveTableOptions>): boolean {
    if (getModuleType(module) !== ModuleType.LiveTable) return false;
    const moduleType = (module as ILiveTableModule).data?.moduleType;
    if (!liveTableOptions || !moduleType) return false;
    const options = Object.values(liveTableOptions).find(option => option.moduleType === moduleType);
    return !!options?.getOnDayRangeChange;
}

export function isGenericChart (module: IModule): boolean {
    const moduleType = getModuleType(module);
    return [
        ModuleType.Chart,
        ModuleType.IndexGroup,
        ModuleType.Leaderboard,
        ModuleType.StorageForecastChart,
    ].includes(moduleType as ModuleType);
}

export function isForecastHistoryChart (module: IModule): boolean {
    const moduleType = getModuleType(module);
    return moduleType === ModuleType.ForecastHistory;
}

export function isNivEvolutionChart (module: IModule): boolean {
    const moduleType = getModuleType(module);
    return moduleType === ModuleType.NivEvolution;
}

export function isAncillaryChart (module: IModule): boolean {
    const moduleType = getModuleType(module);
    return moduleType === ModuleType.AncillaryChart;
}

export function isMetricModule (module: IModule): boolean {
    const moduleType = getModuleType(module);
    return moduleType === ModuleType.Metric
        || moduleType === ModulePresetType.Metric
        || moduleType === ModulePresetType.DialMetric;
}

export function isTreemapModule (module: IModule): boolean {
    const moduleType = getModuleType(module);
    return moduleType === ModuleType.Treemap;
}

export function isChartDateOverrideModule (module: IModule): boolean {
    return isGenericChart(module)
        || isForecastHistoryChart(module)
        || isAncillaryChart(module)
        || isMetricModule(module)
        || isTreemapModule(module)
        || isNivEvolutionChart(module);
}

export function getSubModuleTypeFromModulePreset (modulePreset: IModulePreset): ModuleType | ModulePresetType | BoaTypes | null | BoaIds {
    if (Object.keys(BoaTypes).includes(modulePreset.type)) {
        return (modulePreset.module as IModulePresetBoaDashboard);
    }
    return modulePreset.type;
}

export function updateModulesDate (modules: Array<IModule>, date: IDateObject): void {
    const dateCopy = cloneDeep(date) as IDateObject;

    modules.forEach((module) => {
        const moduleType = getModuleType(module);
        switch (moduleType) {
            case ModuleType.Chart:
                (module as IChartModule).date = dateCopy;
                break;
            case ModuleType.ForecastHistory:
                (module as IForecastHistoryModule).data.dateFormat = dateCopy;
                break;
            case ModuleType.Metric:
            case ModulePresetType.Metric:
            case ModulePresetType.DialMetric:
                (module as IMetricModule).dates = dateFormatToAnyFormat(dateCopy, 'IDateRangeByNumberWithType');
                break;
            case ModuleType.AncillaryChart:
                (module as IAncillaryChartModule).data.date = dateCopy;
                break;
            case ModuleType.Treemap:
            case ModuleType.NivEvolution:
                (module as IGeneralModule).data = {
                    ...((module as IGeneralModule).data || {}),
                    date: dateCopy,
                };
                break;
            default:
        }
    });
}
